import { CAGED_OFFSETS_FOR_C_KEY, CagedLetter } from '../data/caged';
import { CHROMATIC_SHARP, CHROMATIC_FLAT } from '../data/notes';
import { noteToSemitone } from './musicTheory';

const VALID_ROOTS = new Set<string>([
  ...CHROMATIC_SHARP,
  ...CHROMATIC_FLAT,
]);

const VALID_LETTERS: ReadonlySet<CagedLetter> = new Set(['C', 'A', 'G', 'E', 'D']);

/**
 * Resolve a CAGED scale-box window in absolute fret numbers.
 * - For key C: returns the canonical window from CAGED_OFFSETS_FOR_C_KEY.
 * - For other keys: uses formula `barre = (semitones(root) - semitones(letter)) mod 12`,
 *   producing the standard 4-fret window [barre, barre+3].
 *
 * Returns null for null/invalid inputs.
 */
export function computeCagedWindow(
  root: string | null,
  letter: CagedLetter | null,
): { lowFret: number; highFret: number } | null {
  if (!root || !letter) return null;
  if (!VALID_LETTERS.has(letter)) return null;
  if (!VALID_ROOTS.has(root)) return null;

  if (root === 'C') {
    const [low, high] = CAGED_OFFSETS_FOR_C_KEY[letter];
    return { lowFret: low, highFret: high };
  }

  const rootSemi = noteToSemitone(root);
  const letterSemi = noteToSemitone(letter);
  const barre = ((rootSemi - letterSemi) % 12 + 12) % 12;
  return { lowFret: barre, highFret: barre + 3 };
}
